// @ts-nocheck
import {
  Grid2,
  Box,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { init, use } from 'echarts';
import PropTypes from 'prop-types';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
} from 'echarts/components';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';

/**
 * @description This component displays user access statistics using ECharts for pie and bar charts.
 * @param {Object} props.userStats - The user statistics data for the charts.
 * @returns {JSX.Element} The rendered component.
 */

const AccessLogCharts = ({ userStats, fetchUserStats }) => {
  use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    CanvasRenderer,
    SVGRenderer,
  ]);
  const theme = useTheme();
  const DEFAULT_VALUE = 'Login count';
  const LOGIN_COUNT_LAST_30_DAYS = 'Login count (last 30 days)';

  const [value, setValue] = React.useState(DEFAULT_VALUE);

  /**
   * @description Handles the change event of the radio buttons to switch between different chart views (User Type and Login Count).
   * @function handleRadioChange
   * @param {Object} event - event object triggered by the change of radio button.
   * @returns {void}
   */
  const handleRadioChange = useCallback(
    (event) => {
      setValue(event.target.value);
    },
    [setValue]
  );
  const userTypePieChartOption = {
    aria: {
      show: true,
    },
    tooltip: {
      trigger: 'item',
    },
    legend: {
      orient: 'vertical',
      bottom: 'bottom',
    },
    toolbox: {
      feature: {
        saveAsImage: {
          show: true,
          title: 'Download',
          name: 'User_Type_Pie_Chart',
          tabIndex: 0,
        },
      },
    },
    series: [
      {
        name: 'Access From',
        type: 'pie',
        radius: '50%',
        data: userStats['User Status'],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
    color: [
      theme.palette.other.pieChart3,
      theme.palette.other.pieChart6,
      theme.palette.other.pieChart14,
    ],
    responsive: {
      autoResize: true,
      debounce: 50,
    },
  };
  const loginCountBarChartOption = {
    aria: { show: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        label: {
          show: true,
        },
      },
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          show: true,
          title: 'Download',
          name: 'Login_Count_Bar_Chart',
          tabIndex: 0,
        },
      },
    },
    calculable: true,
    legend: {
      data: ['Login Count(Last 30 days)'],
      itemGap: 5,
    },
    grid: {
      top: '12%',
      left: '1%',
      right: '10%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: userStats[LOGIN_COUNT_LAST_30_DAYS]
          ? Object.keys(userStats[LOGIN_COUNT_LAST_30_DAYS])
          : [],
        axisTick: {
          show: false,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'count',
      },
    ],
    dataZoom: [
      {
        show: true,
        start: 0,
        end: 100,
      },
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
      {
        show: true,
        yAxisIndex: 0,
        filterMode: 'empty',
        width: 30,
        height: '80%',
        showDataShadow: false,
        left: '95%',
      },
    ],
    color: [theme.palette.other.pieChart3],
    series: [
      {
        name: 'Login Count(Last 30 days)',
        type: 'bar',
        data: userStats[LOGIN_COUNT_LAST_30_DAYS]
          ? Object.values(userStats[LOGIN_COUNT_LAST_30_DAYS])
          : [],
      },
    ],
    responsive: {
      autoResize: true,
      debounce: 50,
    },
  };
  const refreshOnClick = useCallback(() => {
    fetchUserStats();
  }, [fetchUserStats]);
  useEffect(() => {
    function renderCharts() {
      if (document.getElementById('userTypePieChart')) {
        const userTypePieChart = init(
          document.getElementById('userTypePieChart'),
          null,
          { renderer: 'svg' }
        );
        window.addEventListener('resize', () => {
          userTypePieChart.resize();
        });
        userTypePieChart.setOption(userTypePieChartOption);
        window.addEventListener('resize', () => userTypePieChart.resize());
      }
      if (document.getElementById('loginCountBarChart')) {
        const loginCountBarChart = init(
          document.getElementById('loginCountBarChart'),
          null,
          { renderer: 'svg' }
        );
        loginCountBarChart.setOption(loginCountBarChartOption);
        window.addEventListener('resize', () => loginCountBarChart.resize());
      }
    }
    renderCharts();
    window.addEventListener('resize', renderCharts);
  }, [value, userStats]);

  const customStyles = {
    customBox: {
      backgroundColor: 'other.white',
      padding: 1,
      borderRadius: 2,
      marginBottom: 4,
      border: `1px solid ${theme.palette.other.gray3}`,
    },
    pieChartContainer: {
      height: '480px',
      width: '100%',
      minWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
  };
  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 12 }}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
          >
            <IconButton
              title="Refresh"
              color="primary"
              onClick={refreshOnClick}
            >
              <RefreshIcon />
            </IconButton>
            <FormControl variant="standard">
              <RadioGroup
                title="demo-error-radios"
                name="access logs"
                value={value}
                onChange={handleRadioChange}
                sx={{ display: 'flex', flexDirection: 'row', marginLeft: 1 }}
              >
                <FormControlLabel
                  value="Login count"
                  control={<Radio />}
                  label="Login Count"
                />
                <FormControlLabel
                  value="User Type"
                  control={<Radio />}
                  label="User Type"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          {value === DEFAULT_VALUE && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              tabIndex={0}
            >
              <Typography variant="body1" mr={1}>
                Average login count (Last 30 days):
              </Typography>
              <Typography variant="body1" mr={1}>
                {userStats['Average login count (Last 30 days)']}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid2>
      {value === 'User Type' && (
        <Grid2 size={{ xs: 12 }}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            sx={customStyles.customBox}
          >
            <Box sx={{ width: '100%' }}>
              <div
                id="userTypePieChart"
                style={customStyles.pieChartContainer}
              />
            </Box>
          </Box>
        </Grid2>
      )}
      {value === DEFAULT_VALUE && (
        <Grid2 size={{ xs: 12 }}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            sx={customStyles.customBox}
          >
            <Box sx={{ width: '100%' }}>
              <div
                id="loginCountBarChart"
                style={customStyles.pieChartContainer}
              />
            </Box>
          </Box>
        </Grid2>
      )}
    </Grid2>
  );
};
AccessLogCharts.propTypes = {
  userStats: PropTypes.shape({
    'User Status': PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    'Login count (last 30 days)': PropTypes.objectOf(PropTypes.number),
    'Average login count (Last 30 days)': PropTypes.number,
  }).isRequired,
};

export default AccessLogCharts;
